import { FC, useRef, useState } from 'react';
import { UseAutocompleteProps } from '@mui/material';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { useCreate, useGo, useMany, useModal, useResourceParams } from '@refinedev/core';
import { DeleteButton, useAutocomplete, useDataGrid } from '@refinedev/mui';

import { ATTACKIQ_ID, CONTENT_MODULE_IDS, MODULE_CONTENT_MAP } from '@attackiq/constants';

import { Module, ModuleContent } from '../types';

interface IContentModulesProps {
  contentType: keyof typeof MODULE_CONTENT_MAP;
}

const ContentModules: FC<IContentModulesProps> = ({ contentType }) => {
  const { id } = useResourceParams();
  const { dataGridProps } = useDataGrid<ModuleContent>({
    resource: 'module_contents',

    filters: {
      permanent: [
        {
          field: 'content_id',
          operator: 'eq',
          value: id
        }
      ]
    }
  });
  const modulesIds = dataGridProps.rows.map((item: ModuleContent) => item.module_id);
  const {
    result: modulesData,
    query: { isLoading: isLoadingModules }
  } = useMany<Module>({
    resource: 'modules',
    ids: [...new Set(modulesIds)],
    queryOptions: {
      enabled: modulesIds.length > 0
    }
  });
  const go = useGo();
  const { show: showModal, ...modalProps } = useModal();

  const columns: GridColDef<ModuleContent>[] = [
    {
      field: 'module__display_name',
      headerName: 'Module',
      renderCell: ({ row }) => {
        if (isLoadingModules) return 'Loading...';

        const module = modulesData?.data.find(item => item.id === row.module_id);

        const handleClick = () => {
          go({
            to: {
              resource: 'modules',
              action: 'show',
              id: row.module_id
            }
          });
        };

        return (
          <Link sx={{ cursor: 'pointer' }} onClick={handleClick}>
            {module?.display_name}
          </Link>
        );
      },
      flex: 1
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: ({ row }) => {
        const name = row.content_object?.name || row.content_object?.template_name;
        return (
          <DeleteButton
            recordItemId={row.id}
            resource="module_contents"
            hideText
            confirmTitle={'Do you want to delete Module Content for "' + name + '" ?'}
          />
        );
      },
      sortable: false,
      maxWidth: 100
    }
  ];

  // Remove sorting until https://github.com/refinedev/refine/issues/7099 is fixed
  const { sortingMode, sortModel, onSortModelChange, ...restDataGridProps } = dataGridProps;

  return (
    <>
      <CreateModuleContent contentType={contentType} {...modalProps} existingModuleIds={modulesIds} />
      <Button color="success" variant="contained" onClick={showModal} sx={{ alignSelf: 'flex-end' }}>
        Add To Module
      </Button>
      <DataGridPro {...restDataGridProps} columns={columns} disableColumnFilter pagination />
    </>
  );
};

const CreateModuleContent = ({ contentType, visible, close, existingModuleIds }) => {
  const [module, setModule] = useState<Module | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const warningShownRef = useRef(false);

  const { autocompleteProps } = useAutocomplete<Module>({
    resource: 'modules',
    pagination: {
      pageSize: 10000
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });
  const { id } = useResourceParams();
  const {
    mutateAsync: createMutateAsync,
    mutation: { isPending }
  } = useCreate();

  const handleChange: UseAutocompleteProps<Module, false, false, false>['onChange'] = (_event, value) => {
    setModule(value);
  };

  const handleCreate = async () => {
    if (module) {
      const isAddingInDevelopment = module.id === CONTENT_MODULE_IDS.IN_DEVELOPMENT_MODULE;
      const hasInDevelopment = existingModuleIds.includes(CONTENT_MODULE_IDS.IN_DEVELOPMENT_MODULE);
      const shouldWarn = isAddingInDevelopment || (hasInDevelopment && !warningShownRef.current);

      if (shouldWarn) {
        setShowWarning(true);
        warningShownRef.current = true;
      } else {
        await performCreate();
      }
    }
  };

  const performCreate = async () => {
    if (module) {
      await createMutateAsync({
        resource: 'module_contents',
        values: {
          module_id: module.id,
          content_id: id,
          content_type_model: contentType,
          company_id: ATTACKIQ_ID,
          content_type_app_label: 'ai_cloud'
        }
      });

      handleClose();
    }
  };

  const handleClose = () => {
    close();
    setModule(null);
    setShowWarning(false);
  };

  const handleWarningConfirm = async () => {
    setShowWarning(false);
    await performCreate();
  };

  const handleWarningCancel = () => {
    handleClose();
  };

  return (
    <>
      <Dialog open={visible && !showWarning} onClose={handleClose} fullWidth>
        <DialogTitle>Add {MODULE_CONTENT_MAP[contentType]} to:</DialogTitle>
        <DialogContent>
          <Autocomplete
            {...autocompleteProps}
            sx={{ mt: 0.5 }}
            onChange={handleChange}
            getOptionLabel={option => option.display_name || ''}
            getOptionDisabled={option => existingModuleIds.includes(option.id)}
            renderInput={params => <TextField {...params} label="Module" />}
          />
        </DialogContent>
        <DialogActions>
          <Button color="success" variant="contained" onClick={handleCreate} disabled={!module || isPending}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showWarning} onClose={handleWarningCancel} fullWidth>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          The In Development module modifies the visibility of content in our platform. Please, confirm with the content
          team if you are not sure about this action.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningCancel}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleWarningConfirm} disabled={isPending}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContentModules;
